import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Check, Trash2, Loader2, ChevronLeft, ChevronRight, Search, ArrowUpDown, Plug, ScrollText } from "lucide-react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TextField } from "@/components/kit/fields";
import { DateRangePicker } from "@/components/kit/DateRangePicker";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/format";

// Telegram integration lives in Settings → Security (it powers security alerts and password
// recovery). Connection / token / test logic is unchanged — only its placement moved.
export function TelegramIntegrationCard({ embedded = false }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["telegram"], queryFn: async () => (await http.get("/telegram")).data });
  const integration = data?.integration;
  const connected = !!integration?.connected;

  const [token, setToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  useEffect(() => { if (data) setChatId(integration?.chat_id || ""); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const test = async () => {
    setTesting(true);
    try {
      const res = await http.post("/telegram/test", { bot_token: token || undefined, chat_id: chatId });
      toast.success(res.data.message || "Test message successfully delivered.", { description: res.data.bot_username ? `@${res.data.bot_username}` : undefined });
    } catch (e) { toast.error(e?.response?.data?.detail || "Telegram authorization failed."); }
    finally { setTesting(false); }
  };

  const connect = async () => {
    setSaving(true);
    try {
      await http.post("/telegram", { bot_token: token || undefined, chat_id: chatId, events: integration?.events || [], enabled: true });
      setToken("");
      qc.invalidateQueries({ queryKey: ["telegram"] });
      toast.success("Telegram connected");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save — test failed"); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    await http.delete("/telegram");
    setToken(""); setChatId("");
    qc.invalidateQueries({ queryKey: ["telegram"] });
    toast.success("Telegram disconnected");
  };

  return (
    <div className={embedded ? "" : "rounded-xl border border-white/[0.08] bg-[#141417] p-5"} data-testid="telegram-integration-card">
      <div className="mb-3 flex items-center gap-2.5">
        <Plug className="h-4 w-4 text-[#38BDF8]" /><h3 className="text-sm font-medium text-foreground">Telegram integration</h3>
        {connected && <StatusBadge status="approved" label="Connected" />}
        {connected && (
          <Button variant="outline" size="sm" onClick={() => setLogsOpen(true)} data-testid="tg-check-logs"
            className="ml-auto h-7 gap-1.5 border-white/[0.12] text-xs text-foreground hover:bg-white/[0.04]">
            <ScrollText className="h-3.5 w-3.5" /> Check logs
          </Button>
        )}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Connect your own bot via <span className="text-foreground">@BotFather</span> — used for security alerts, notifications and password recovery.
        {connected && <> Linked to <span className="text-foreground">@{integration.bot_username}</span>.</>}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Bot Token" value={token} onChange={setToken} testId="tg-token"
          placeholder={integration?.token_mask ? `Saved: ${integration.token_mask} (blank = keep)` : "7753...:AA...K7FU"} />
        <TextField label="Chat ID" value={chatId} onChange={setChatId} testId="tg-chat" placeholder="-1003660341029" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={test} disabled={testing || (!token && !connected) || !chatId} data-testid="tg-test" className="gap-1.5 border-white/[0.12] text-foreground hover:bg-white/[0.04]">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Test
        </Button>
        <Button size="sm" onClick={connect} disabled={saving || (!token && !connected) || !chatId} data-testid="tg-save" className="gap-1.5 bg-white text-black hover:bg-white/90">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
        </Button>
        {connected && <Button variant="ghost" size="sm" onClick={disconnect} data-testid="tg-disconnect" className="ml-auto gap-1.5 text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="h-4 w-4" /> Disconnect</Button>}
      </div>
      <p className="mt-2.5 text-xs text-muted-foreground/70">Saved only after a successful test. Token is encrypted and never shown in full.</p>

      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-w-4xl border-white/[0.08] bg-[#141417]" data-testid="tg-logs-modal">
          <DialogHeader>
            <DialogTitle className="text-foreground">Telegram event logs</DialogTitle>
            <DialogDescription className="text-muted-foreground">Every delivery attempt — see instantly why a message did or didn't arrive.</DialogDescription>
          </DialogHeader>
          <TelegramEventLogs />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function TelegramEventLogs() {
  const [q, setQ] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [sort, setSort] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detail, setDetail] = useState(null);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize, sort };
    if (q.trim()) p.q = q.trim();
    if (range.from) p.date_from = range.from;
    if (range.to) p.date_to = range.to;
    return p;
  }, [q, range, sort, page, pageSize]);

  const { data } = useQuery({ queryKey: ["telegram-logs", params], queryFn: async () => (await http.get("/telegram/logs", { params })).data, keepPreviousData: true });
  const rows = data?.items || [];
  const total = data?.total || 0;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="telegram-logs">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search events..." data-testid="tg-log-search" className="h-9 rounded-lg border-white/[0.1] bg-[#0A0A0A] pl-8 text-sm" />
        </div>
        <div className="w-full sm:w-[190px]"><DateRangePicker from={range.from} to={range.to} onChange={(r) => { setRange(r); setPage(1); }} placeholder="Date range" testId="tg-log-range" /></div>
        <Button size="sm" variant="ghost" onClick={() => setSort((s) => (s === "desc" ? "asc" : "desc"))} data-testid="tg-log-sort" className="h-9 gap-1.5 rounded-lg px-2.5 text-sm text-muted-foreground hover:text-foreground"><ArrowUpDown className="h-3.5 w-3.5" /> {sort === "desc" ? "Newest" : "Oldest"}</Button>
      </div>
      <div className="overflow-hidden rounded-lg border border-white/[0.06]">
        <div className="max-h-[50vh] overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[560px]" data-testid="tg-log-table">
            <thead className="sticky top-0 border-b border-white/[0.06] bg-[#17171b]">
              <tr>{["Time", "Event", "Status", "Message"].map((h) => <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">No Telegram events yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} onClick={() => setDetail(r)} data-testid={`tg-log-row-${r.id}`} className="cursor-pointer hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-3 py-2.5 text-sm text-muted-foreground">{fmtDateTime(r.created_at)}</td>
                  <td className="px-3 py-2.5 text-sm text-foreground/90">{(r.event || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={r.ok ? "approved" : "trash"} label={r.ok ? "Delivered" : "Failed"} /></td>
                  <td className="px-3 py-2.5 text-sm text-muted-foreground">{r.ok ? (r.message_id ? `Message ID ${r.message_id}` : "Delivered") : (r.error || `HTTP ${r.http_status}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] px-3 py-2.5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="text-xs">Rows</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger data-testid="tg-log-page-size" className="h-8 w-[68px] border-white/[0.1] bg-[#0A0A0A] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs">{total === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}</span>
            <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="tg-log-prev" className="h-8 px-2 text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-xs">{page}/{maxPage}</span>
            <Button size="sm" variant="ghost" disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)} data-testid="tg-log-next" className="h-8 px-2 text-muted-foreground hover:text-foreground"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto border-white/[0.08] bg-[#141417] sm:max-w-md" data-testid="tg-log-detail">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-foreground">{(detail.event || "").replace(/_/g, " ")}</SheetTitle>
                <SheetDescription className="sr-only">Telegram delivery details</SheetDescription>
                <div className="pt-1"><StatusBadge status={detail.ok ? "approved" : "trash"} label={detail.ok ? "Delivered" : "Failed"} /></div>
              </SheetHeader>
              <div className="mt-5 space-y-4">
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Message</h4>
                  <div className="whitespace-pre-line rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3 text-sm text-foreground/90">{detail.message || "—"}</div>
                </div>
                <div className="divide-y divide-white/[0.05] rounded-lg border border-white/[0.06] bg-[#0A0A0A]">
                  {[["Bot", detail.bot_username ? `@${detail.bot_username}` : "—"], ["Chat ID", detail.chat_id], ["Message ID", detail.message_id], ["HTTP status", detail.http_status], ["Response time", detail.response_ms != null ? `${detail.response_ms} ms` : "—"], ["Sent at", fmtDateTime(detail.created_at) + " UTC"]].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-4 px-3 py-2 text-sm"><span className="text-muted-foreground/70">{k}</span><span className="font-mono text-xs text-foreground/80">{v ?? "—"}</span></div>
                  ))}
                </div>
                <div>
                  <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Telegram API response</h4>
                  <div className="break-all rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3 font-mono text-xs text-muted-foreground">{detail.api_response || detail.error || "—"}</div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
